import { useState } from 'react';
import HologramCard from '../../../components/base/HologramCard';
import GlitchText from '../../../components/base/GlitchText';

export default function ExperienceSection() {
  const [selectedMemory, setSelectedMemory] = useState<string | null>(null);

  const experiences = [
    {
      id: 'nokasa',
      company: 'NoKasa',
      role: 'Software Development Intern',
      period: 'May 2025 - June 2025',
      type: 'INTERNSHIP',
      description: 'Led product improvements and cross-functional collaboration in a fast-paced startup environment.',
      achievements: [
        'Improved usability and accessibility with pixel-perfect UI from Figma, incorporating customer feedback cycles',
        'Optimized user experience by refining success criteria, user personas, and potential user workflows',
        'Conducted root-cause analyses, implemented fixes, and ensured fast iteration through Agile workflows',
        'Standardized branding and UX by enhancing UI components, buttons, spacing, customization options and improving overall engagement'
      ],
      technologies: ['Agile', 'Figma', 'UI/UX', 'Customer Feedback', 'Root-cause Analysis'],
      color: 'cyan',
      icon: 'ri-building-line',
      status: 'COMPLETED'
    }
  ];

  return (
    <section id="experience" className="min-h-screen py-20 bg-gradient-to-b from-black to-gray-900">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white font-orbitron mb-4">
            <GlitchText text="EXPERIENCE LOG" />
          </h2>
          <div className="text-xl text-blue-400 font-mono mb-2">PROFESSIONAL JOURNEY DATABASE</div>
          <p className="text-gray-400 font-rajdhani text-lg">Career milestones and achievements</p>
        </div>

        {/* Experience Timeline */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-white font-orbitron mb-8 text-center">WORK EXPERIENCE</h3>
          <div className="max-w-4xl mx-auto">
            {experiences.map((exp, index) => (
              <HologramCard 
                key={exp.id}
                glowColor={exp.color}
                onClick={() => setSelectedMemory(selectedMemory === exp.id ? null : exp.id)}
                className="mb-6 cursor-pointer"
              >
                <div className="space-y-4">
                  {/* Experience Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <i className={`${exp.icon} text-3xl text-${exp.color}-400`}></i>
                      <div>
                        <h4 className="text-xl font-bold text-white font-orbitron">{exp.role}</h4>
                        <div className="text-lg text-cyan-400 font-mono">{exp.company}</div>
                        <div className="text-sm text-gray-400 font-rajdhani">{exp.period}</div>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded text-sm font-mono bg-${exp.color}-500/20 text-${exp.color}-400`}>
                      {exp.type}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-300 font-rajdhani leading-relaxed">
                    {exp.description}
                  </p>

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2">
                    {exp.technologies.map((tech, techIndex) => (
                      <span 
                        key={techIndex}
                        className={`px-2 py-1 rounded text-xs font-mono bg-${exp.color}-500/10 text-${exp.color}-400 border border-${exp.color}-500/30`}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Expanded Achievements */}
                  {selectedMemory === exp.id && (
                    <div className="mt-6 pt-4 border-t border-gray-700 animate-fade-in">
                      <h5 className="text-lg font-bold text-white font-orbitron mb-4">KEY ACHIEVEMENTS:</h5>
                      <ul className="space-y-3">
                        {exp.achievements.map((achievement, achIndex) => (
                          <li key={achIndex} className="flex items-start space-x-3">
                            <div className={`w-2 h-2 bg-${exp.color}-400 rounded-full mt-2 flex-shrink-0`}></div>
                            <span className="text-gray-300 font-rajdhani">{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </HologramCard>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
